import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { CONTACT } from "../../data/cruiseData";
import { SEO_PAGES } from "../../data/seoPages";
import TrustBar from "../TrustBar";

const PRIMARY = [
 { to: "/", label: "Home" },
 { to: "/rates-and-menus", label: "Magic Hire Rates" },
 { to: "/cruises", label: "Cruises", hasDropdown: "cruises" },
 { to: "/ticketed-cruises", label: "Ticketed Cruises", hasDropdown: "tickets" },
 { to: "/event-cruises", label: "Event Cruises" },
 { to: "/blog", label: "Latest" },
 { to: "/contact", label: "Contact" },
];

const CRUISE_TYPES = [
  { label: "Corporate Cruises",  slug: "corporate-cruises-sydney-harbour" },
  { label: "Wedding Cruises",    slug: "wedding-cruises-sydney-harbour" },
  { label: "Christmas Cruises",  slug: "christmas-party-cruises-sydney" },
  { label: "BYO Cruises",        slug: "__byo__" },
];

const TICKETED_TYPES = [
  { label: "NYE Cruises Sydney",         slug: "nye-cruises-sydney-harbour",         pill: "Hot" },
  { label: "Vivid Sydney Cruises",       slug: "vivid-cruises-sydney" },
  { label: "Australia Day Cruises",      slug: "australia-day-cruises-sydney-harbour" },
  { label: "Boxing Day Cruises",         slug: "boxing-day-cruises-sydney" },
];

const LOGO = "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/2ber7617_magic-cruises-sydney-harbour.png";

export default function Navbar() {
 const [open, setOpen] = useState(false);
 const [drop, setDrop] = useState(null); // 'cruises' | 'events' | null
 const [mDrop, setMDrop] = useState(null); // 'cruises' | 'events' | null
 const [scrolled, setScrolled] = useState(false);
 const loc = useLocation();
 const isHome = loc.pathname === "/";

 useEffect(() => {
 const onScroll = () => setScrolled(window.scrollY > 60);
 onScroll();
 window.addEventListener("scroll", onScroll, { passive: true });
 return () => window.removeEventListener("scroll", onScroll);
 }, []);

 // Dark translucent nav at top of home; white nav when scrolled or on subpages
 const dark = isHome && !scrolled;
 const bg = dark ? "bg-[hsla(220,60%,8%,0.55)] backdrop-blur-md" : "bg-white/95 backdrop-blur-md border-b border-[hsla(220,20%,82%,0.6)]";
 const inkClr = dark ? "text-white" : "text-ink";
 const muteClr = dark ? "text-white/75" : "text-ink-soft";

 return (
 <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
 <TrustBar />
 <div className={`transition-colors duration-300 ${bg}`}>
 <div className="mx-auto max-w-7xl px-5 lg:px-10 py-3 flex items-center justify-between gap-4">
 {/* LOGO, image only, image contains the "Magic Cruises" wordmark */}
 <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
 <img src={LOGO} alt="Magic Cruises Sydney Harbour" className={`h-12 lg:h-14 w-auto object-contain shrink-0 ${dark ? "brightness-0 invert" : ""}`} />
 <div className={`hidden sm:block leading-tight ${dark ? "text-white/85" : "text-ink-mute"}`}>
 <div className="text-[9px] uppercase tracking-[0.32em] font-bold">Sydney Harbour</div>
 <div className="text-[9px] uppercase tracking-[0.32em] font-bold">Private Charter</div>
 </div>
 </Link>

 {/* NAV */}
 <nav className="hidden lg:flex items-center gap-6 whitespace-nowrap">
 {PRIMARY.map((l, idx) => (
 l.hasDropdown ? (
 <div key={`${l.label}-${idx}`} className="relative" onMouseEnter={() => setDrop(l.hasDropdown)} onMouseLeave={() => setDrop(null)} data-testid={`nav-${l.hasDropdown}-wrapper`}>
 <NavLink to={l.to} className={({isActive}) => `text-[12px] uppercase tracking-[0.2em] font-bold flex items-center gap-1 transition-colors ${(isActive && l.hasDropdown === "cruises") ? "text-pink" : `${inkClr} hover:text-pink`}`} data-testid={`nav-${l.label.toLowerCase()}`}>
 {l.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${drop === l.hasDropdown ? "rotate-180" : ""}`} />
 </NavLink>
 {drop === l.hasDropdown && (
 <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50 w-[300px]" data-testid={`${l.hasDropdown}-mega-menu`}>
  <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-[hsla(220,20%,82%,0.7)] overflow-hidden">
   <ul className="py-2" data-testid={`${l.hasDropdown}-types-list`}>
    {(l.hasDropdown === "cruises" ? CRUISE_TYPES : TICKETED_TYPES).map((t) => (
     <li key={t.slug}>
      <Link
       to={t.slug === "__byo__" ? "/cruises/byo-cruise-sydney-harbour" : `/cruises/${t.slug}`}
       onClick={() => setDrop(null)}
       className="group flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-[hsl(199,85%,96%)] hover:text-pink transition-colors"
       data-testid={`${l.hasDropdown}-type-${t.slug}`}
      >
       <span className="flex items-center gap-2">{t.label}{t.pill && <span className="text-[9px] uppercase tracking-[0.2em] font-black bg-amber-400 text-[#0A1E3D] px-1.5 py-0.5 rounded-full">{t.pill}</span>}</span>
       <span className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-pink text-xs">→</span>
      </Link>
     </li>
    ))}
    {l.hasDropdown === "cruises" && (
     <li className="border-t border-[hsla(220,20%,90%,0.7)] mt-1 pt-1">
      <Link to="/event-cruises" onClick={() => setDrop(null)} className="group flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-[hsl(199,85%,96%)] hover:text-pink transition-colors" data-testid="cruises-type-all-events">
       <span>See all event cruises</span>
       <span className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-pink text-xs">→</span>
      </Link>
     </li>
    )}
   </ul>
   <Link to={l.hasDropdown === "cruises" ? "/event-cruises" : "/ticketed-cruises"} onClick={() => setDrop(null)} className={`block text-white text-center py-3 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${l.hasDropdown === "tickets" ? "bg-[#0A1E3D] hover:bg-[#142a4d]" : "bg-[hsl(199,85%,47%)] hover:bg-[hsl(199,85%,42%)]"}`}>
    {l.hasDropdown === "cruises" ? "View All Event Cruises →" : "View All Ticketed Cruises →"}
   </Link>
  </div>
 </div>
 )}
 </div>
 ) : (
 <NavLink key={`${l.label}-${idx}`} to={l.to} end={l.to === "/"} className={({isActive}) => `text-[12px] uppercase tracking-[0.2em] font-bold transition-colors ${isActive ? "text-pink" : `${inkClr} hover:text-pink`}`} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}>
 {l.label}
 </NavLink>
 )
 ))}
 </nav>

 <div className="hidden lg:flex items-center gap-4 shrink-0">
 <Link to="/contact" className="btn-blue" data-testid="header-enquire-btn">Book Now</Link>
 </div>

 <button onClick={() => setOpen(!open)} className={`lg:hidden ${inkClr} flex items-center gap-2 px-3 py-2 rounded-full ring-1 ring-current/20 hover:ring-current/40 transition-all`} data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={open}>
 {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
 <span className="text-[11px] uppercase tracking-[0.22em] font-black">{open ? "Close" : "Menu"}</span>
 </button>
 </div>
 </div>

 {open && (
 <div className="lg:hidden bg-cream border-b border-[hsla(220,20%,82%,0.5)] max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
 <div className="px-5 py-6 flex flex-col gap-4">
 {PRIMARY.map((l, idx) => (
 l.hasDropdown ? (
 <div key={`${l.label}-m-${idx}`}>
 <button onClick={() => setMDrop(mDrop === l.hasDropdown ? null : l.hasDropdown)} className="w-full flex items-center justify-between text-sm uppercase tracking-widest font-bold text-ink">
 {l.label} <ChevronDown className={`w-4 h-4 transition-transform ${mDrop === l.hasDropdown ? "rotate-180" : ""}`} />
 </button>
 {mDrop === l.hasDropdown && (
 <div className="mt-3 pl-3 border-l border-[hsla(220,20%,82%,0.6)] space-y-2">
  {(l.hasDropdown === "cruises" ? CRUISE_TYPES : TICKETED_TYPES).map(t => (
   <Link key={t.slug} to={t.slug === "__byo__" ? "/cruises/byo-cruise-sydney-harbour" : `/cruises/${t.slug}`} onClick={() => setOpen(false)} className="flex items-center justify-between gap-2 text-sm text-ink-soft hover:text-pink font-medium"><span className="flex items-center gap-2">{t.label}{t.pill && <span className="text-[9px] uppercase tracking-[0.2em] font-black bg-amber-400 text-[#0A1E3D] px-1.5 py-0.5 rounded-full">{t.pill}</span>}</span></Link>
  ))}
  <Link to={l.hasDropdown === "cruises" ? "/event-cruises" : "/ticketed-cruises"} onClick={() => setOpen(false)} className="block text-xs uppercase tracking-widest font-bold text-pink pt-2">{l.hasDropdown === "cruises" ? "View All Event Cruises →" : "View All Ticketed Cruises →"}</Link>
 </div>
 )}
 </div>
 ) : (
 <NavLink key={`${l.label}-m-${idx}`} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} className={({isActive}) => `text-sm uppercase tracking-widest font-bold ${isActive ? "text-pink" : "text-ink"}`}>
 {l.label}
 </NavLink>
 )
 ))}
 <a href={`tel:${CONTACT.phone1}`} className="text-ink-soft text-sm mt-2 font-bold">{CONTACT.phone1}</a>
 <Link to="/contact" onClick={() => setOpen(false)} className="inline-flex justify-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--pink))] text-white text-xs uppercase tracking-widest font-bold">Book Now</Link>
 </div>
 </div>
 )}
 </header>
 );
}
